/**
 * Copy of the Focus Search Term pages (`components/Ams/focus/`). VentaHub's
 * English, verbatim where VentaHub had it: the sheet and its history
 * (`components/features/focus-search-term/`), the page shell
 * (`pages/FocusedData/FocusSearchTermSheetPage.tsx`), the Search Term Data form
 * (`pages/FocusedData/SearchTermDataPage.tsx`) and its history
 * (`pages/FocusedData/SearchTermHistory.tsx`).
 */
export const focusTerms = {
  page: {
    title: 'Focus Search Term Sheet',
    crumb: 'Search Term Sheet',
    descriptionSheet:
      'One row per search term for a completed week — expand a row for its per-ASIN split.',
    descriptionHistory:
      'One row per frozen week for a single search term — expand a row for that week’s per-ASIN split.',
    views: { sheet: 'Sheet', history: 'Snapshot History' },
    viewsLabel: 'Search term view',
    viewHints: {
      sheet: 'Every search term for one completed week',
      history: 'One search term across every frozen week',
    },
    rerun: 'Re-run snapshot',
    rerunning: 'Re-running…',
    rerunHint: 'Re-run the latest completed week for this store',
    rerunFailed: 'Failed to run the snapshot',
    pickStore: 'Select a store to load its focus search terms.',
  },
  sheet: {
    week: 'Week',
    brands: 'Brands',
    search: 'Search',
    searchPlaceholder: 'Term, ASIN or title',
    readOnly: 'Read-only week',
    readOnlyHint: 'Only the latest frozen week can be edited; older weeks are history.',
    discard: 'Discard',
    save: 'Save',
    saveCount: 'Save ({{count}})',
    saving: 'Saving…',
    saved_one: 'Saved {{count}} term',
    saved_other: 'Saved {{count}} terms',
    saveFailed: 'Failed to save',
    stale: 'Someone saved these terms first — the sheet has been reloaded.',
    empty: 'No focus search terms for this week.',
    emptyHint: 'Nothing matches this search or these brands in the week.',
    noWeeksTitle: 'No frozen week yet',
    noWeeks:
      'No week has been frozen for this store yet. Add terms on the Search Term Data form, then re-run the snapshot.',
    total_one: '{{count}} term',
    total_other: '{{count}} terms',
    expand: 'Expand {{term}}',
    collapse: 'Collapse {{term}}',
    bidActionFor: 'Bid action for {{term}}',
    actionTakenFor: 'Action taken for {{term}}',
    pastFor: 'Earlier bid actions for {{term}}',
    past: {
      title: 'Earlier bid actions',
      description:
        'Bid Action and Action Taken recorded for “{{term}}” in the weeks before {{week}}.',
      week: 'Week',
      bidAction: 'Bid Action',
      actionTaken: 'Action Taken',
      edited: 'Last edited',
      emptyTitle: 'No earlier bid actions',
      empty: 'Nothing was recorded for this term in earlier weeks.',
    },
    none: '—',
    sortBy: 'Sort by {{column}}',
    coverageShort:
      'These {{count}} ASINs account for {{coverage}}% of the term’s clicks. The rest went to listings that drew none, or was trimmed by the top-30 cap.',
    coverageFull: '{{count}} ASINs, covering all of the term’s clicks.',
    ours: 'Ours',
    oursHint: 'We hold the top sponsored slot — this is our own listing, not a rival.',
    formRecorded: 'Recorded on the form',
    formRecordedOn: 'Recorded on the form on {{date}}',
    splitNote:
      'Per-ASIN split — apportioned by each listing’s share of its ad group’s clicks. Amber rows are estimates, not figures Amazon reported per ASIN.',
    splitCapped: 'Showing the top {{shown}} by spend of {{count}}.',
    stockSplit: 'Warehouse {{warehouse}} + FBA {{fba}} + in transit {{transit}}',
    yes: 'Yes',
    no: 'No',
  },
  columns: {
    searchTerm: 'Search Term',
    asins: 'ASINs',
    sas: 'SAS',
    compAsin: 'Comp ASIN',
    compRating: 'Comp Rating',
    compCount: 'Comp Count',
    compRatings: 'Comp Ratings',
    compPrice: 'Comp Price',
    compAsp: 'Comp ASP',
    impressions: 'Impressions',
    clicks: 'Clicks',
    ctr: 'CTR',
    spend: 'Spends',
    units: 'Units',
    cvr: 'CVR',
    sales: 'Sales',
    asp: 'ASP',
    acos: 'ACOS',
    targetAcos: 'Target ACOS',
    targetConversion: 'Target Conv',
    formAction: 'Form Action',
    bidAction: 'Bid Action',
    actionTaken: 'Action Taken',
    week: 'Week',
  },
  help: {
    searchTerm:
      'The query a shopper typed, for the frozen week. Every figure on this row is what Amazon reported for the term across all ad groups that served it — never an estimate. Expand the row for the per-ASIN split, which is.',
    asins:
      'Advertised ASINs that drew a click on this term. Expand to see them — at most the top 30 by spend.',
    sas: 'Sponsored Ad Slot share — of the times we scraped this term’s results page in the last 30 days, how often one of our listings held a sponsored slot. Hover a value for the sample it is built on; the window yields only a handful of scrapes.',
    compAsin:
      'ASIN holding the top sponsored slot on the latest scrape. Flagged when that is one of ours.',
    compRating: 'That competitor’s star rating, as scraped.',
    compCount: 'How many ratings that average is built on.',
    compPrice: 'That competitor’s listed price at scrape time.',
    compAsp:
      'The competitor’s observed price. Their true average selling price is not observable — we never see their unit counts.',
    impressions:
      'Times an ad for this term was shown, summed across every ad group of the store that served it — Amazon’s reported figure, not an estimate.',
    clicks:
      'Clicks the term drew in the week, as Amazon reported it. Expand the row to see how they split across the listings it advertised.',
    ctr: 'Clicks ÷ impressions. Blank when there were no impressions — undefined, not zero.',
    spend:
      'Ad spend on this term for the week, as Amazon reported it. This is the number to weigh against Target ACOS.',
    units: 'Units attributed to the term.',
    cvr: 'Units ÷ clicks. Blank when there were no clicks.',
    sales:
      'Sales value Amazon attributed to this term within its attribution window — not necessarily revenue booked in this week.',
    asp: 'Sales ÷ units — the average price the term actually sold at.',
    acos: 'Spend ÷ sales. Blank when the term made no sales — undefined, not zero.',
    targetAcos: 'Spend-weighted mean of the target ACOS on the ad groups serving this term.',
    targetConversion: 'Spend-weighted mean of the target conversion rate.',
    formAction:
      'The bid action recorded on the Search Term Data form, on the day someone filled it in. Read-only here — the editable decision is the next column.',
    bidAction:
      'What was done about this term. One decision per term — a bid is set on a keyword, not per ASIN.',
    actionTaken: 'Free-text detail of the action.',
    week: 'The Sunday–Saturday week this row describes, frozen once and never recomputed. Newest first.',
    historyAsins:
      'Focused ASINs that drew traffic for this term in the week. Amber when they account for less than all of its clicks.',
    historySas:
      'Sponsored Ad Slot share — how often any of our listings held one of the five paid placements across the 30 days of scrapes before this snapshot.',
    historyFormAction:
      'The bid action recorded on the Search Term Data form. One record per term, so it repeats down every week — it is not weekly data.',
    historyBidAction:
      'What Ops decided about this term in that week. Read-only here — edit it on the Sheet tab, which allows the latest week only.',
  },
  children: {
    asin: { label: 'ASIN', help: 'The advertised listing this share of the term is credited to.' },
    title: { label: 'Title', help: 'Listing title, as held in the catalogue right now.' },
    rating: {
      label: 'Rating',
      help: 'Current star rating. Read live, so an older week still shows today’s rating — the source table keeps no history.',
    },
    ratingCount: {
      label: 'Rating Count',
      help: 'How many ratings that average is built on, currently.',
    },
    sellingPrice: {
      label: 'Selling Price',
      help: 'What a shopper would pay now: an active sale price, else the regular price. Point-in-time, not the price during the week.',
    },
    stock: {
      label: 'Stock',
      help: 'Warehouse + marketplace (FBA) + in-transit units, store-wide. FBA counts for seller accounts only — a vendor account’s marketplace figure is units on order, which nobody holds yet. Hover a value for the split.',
    },
    aPlus: {
      label: 'A+',
      help: 'Whether the listing has A+ content — the scraper’s verdict if it has spoken in the last 30 days, else the latest catalogue analysis.',
    },
    sas: {
      label: 'SAS',
      help: 'Sponsored Ad Slot share — how often this listing held one of the five paid placements across the last 30 days of scrapes of this term. Hover a value for the sample size; a dash means the term was never scraped, which is not the same as losing.',
    },
    impressions: {
      label: 'Impressions',
      help: 'This listing’s apportioned share of the term’s impressions. Amazon reports no per-ASIN split, so it is derived from the ad group’s click share.',
    },
    clicks: {
      label: 'Clicks',
      help: 'Apportioned share of the term’s clicks, by the same weights.',
    },
    ctr: {
      label: 'CTR',
      help: 'Clicks ÷ impressions. On an amber row this is the ad group’s CTR repeated — spend and clicks split by identical weights, so the rate carries no per-ASIN signal. Read the absolute columns instead.',
    },
    spend: { label: 'Spends', help: 'Apportioned share of the term’s ad spend.' },
    units: {
      label: 'Units',
      help: 'Apportioned share of units, split by largest remainder so the parts add back to the term’s exact total — never a unit invented or lost.',
    },
    cvr: {
      label: 'CVR',
      help: 'Units ÷ clicks. Varies between rows mostly from integer rounding, not real per-ASIN performance.',
    },
    sales: {
      label: 'Sales',
      help: 'Apportioned share of the sales Amazon attributed to the term.',
    },
    asp: {
      label: 'ASP',
      help: 'Sales ÷ units for this listing. Blank when it sold nothing — undefined, not zero.',
    },
    acos: {
      label: 'ACOS',
      help: 'Spend ÷ sales. Like CTR, on an amber row this mirrors the ad group’s own ACOS rather than telling you anything specific to this ASIN.',
    },
  },
  sas: {
    never:
      'Not scraped in the last 30 days — no share to report. This is missing data, not a lost slot.',
    held_one: 'Held a sponsored slot on {{wins}} of {{count}} scrape in the last 30 days.',
    held_other: 'Held a sponsored slot on {{wins}} of {{count}} scrapes in the last 30 days.',
    thin: 'Too few looks to read much into.',
  },
  manual: {
    recorded:
      'Recorded on the Search Term Data form — a snapshot of that day’s results page, not this week’s.',
    recordedOn:
      'Recorded on the Search Term Data form on {{date}} — a snapshot of that day’s results page, not this week’s.',
    ours: 'Ours',
    comp1: 'Competitor 1 (form)',
    comp2: 'Competitor 2 (form)',
    days: '{{count}}d',
  },
  history: {
    term: 'Search term',
    termPlaceholder: 'Type to filter terms',
    termHint_one: '{{count}} week · {{spend}} total spend',
    termHint_other: '{{count}} weeks · {{spend}} total spend',
    frozenWeeks_one: '{{count}} frozen week',
    frozenWeeks_other: '{{count}} frozen weeks',
    pickTerm: 'Pick a search term to see how it moved across previous weeks.',
    noTerms: 'No search term in this store has a frozen week yet.',
    noWeeks: 'No frozen weeks for this term.',
    expand: 'Expand week {{week}}',
    collapse: 'Collapse week {{week}}',
    coverageShort:
      'These {{count}} ASINs account for {{coverage}}% of the week’s clicks. The rest went to listings that drew none, or was trimmed by the top-30 cap.',
    coverageFull: '{{count}} ASINs, covering all of the week’s clicks.',
    splitNote:
      'Per-ASIN split for this week — apportioned by each listing’s share of its ad group’s clicks. Amber rows are estimates, not figures Amazon reported per ASIN.',
    oursHint: 'We held the top sponsored slot — this is our own listing, not a rival.',
    actionTaken: 'Action taken: {{text}}',
    noDetail: 'No further detail was recorded.',
  },
  form: {
    title: 'Insert search term data',
    description: 'Capture competitor positions and pricing for a search term across a marketplace.',
    crumb: 'Insert search term data',
    viewHistory: 'View history',
    store: 'Store',
    storePlaceholder: 'Select a store',
    storeRequired: 'Please select a store',
    searchTerm: 'Search Term',
    searchTermRequired: 'Search term is required',
    yourProduct: 'Your Product',
    competitor1: 'Competitor 1',
    competitor2: 'Competitor 2',
    retailerSku: 'Retailer SKU',
    price: 'Price',
    deliveryDays: 'Delivery Days',
    slot: 'Slot',
    slotPlaceholder: 'Select a slot',
    slots: { organic: 'organic', tos: 'tos', ros: 'ros', ba: 'ba' },
    required: 'Required',
    bidAction: 'Bid Action',
    bidActionHint: 'Select the appropriate bid action:',
    bidActionRequired: 'Please select a bid action',
    bidActions: {
      bid_inc: 'Increased Bid',
      bid_dec: 'Decreased Bid',
      pause: 'Paused Bid',
      no_action: 'No Action',
    },
    save: 'Save Comparison Data',
    saving: 'Saving...',
    saved: 'Search Term Data uploaded successfully!',
    saveFailed: 'Upload failed',
    incomplete: 'Please fill in all required fields',
  },
  entries: {
    title: 'Your search term history',
    description: 'Past competitor-pricing comparisons you’ve pulled for the selected date range.',
    crumb: 'Your history',
    formCrumb: 'Search Term Data',
    dates: 'Dates',
    search: 'Search',
    empty: 'No search term data in this date range.',
    columns: {
      store: 'Store',
      date: 'Date',
      searchTerm: 'Search Term',
      clientSku: 'Client SKU',
      clientPrice: 'Client Price',
      clientDeliveryDays: 'Client Delivery Days',
      clientSlot: 'Client Slot',
      comp1Sku: 'Comp1 SKU',
      comp1Price: 'Comp1 Price',
      comp1DeliveryDays: 'Comp1 Delivery Days',
      comp1Slot: 'Comp1 Slot',
      comp2Sku: 'Comp2 SKU',
      comp2Price: 'Comp2 Price',
      comp2DeliveryDays: 'Comp2 Delivery Days',
      comp2Slot: 'Comp2 Slot',
      bidAction: 'Bid Action',
    },
  },
} as const
